import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * "Show all" on a list page — whether the reader wants every matching row in
 * one list instead of a page of them — remembered AGAINST THE ACCOUNT.
 *
 * ## Where it lives
 *
 * **`preferences.showAll.<pageKey>`**, a SIBLING of `preferences.tables` and
 * `preferences.columns`, for the reason `use-column-layout.ts` gives at length:
 * `tables` is the saved-filter scopes, and its whole-set replace treats every
 * key it finds as one more filter to delete. ⛔ **Not `localStorage`, not even
 * as a cache** — a second authority beside the server is how a person on a
 * second device gets whichever loaded last.
 *
 * The value is a SCALAR (`true`), so a write needs no baseline: the API merges
 * an object over an object recursively, a scalar replaces, and `null` deletes.
 * Switching it off sends `null`, so "off" leaves nothing behind in the document.
 *
 * ⚠️ **A failed read is "off", never an error.** The rows are the page; this is
 * how many of them. An unreachable preferences document must not take a list
 * page with it.
 */

const SHOW_ALL_KEY = 'showAll';

/**
 * The read every mount shares while one is in the air — concurrent dedupe,
 * dropped the moment it settles, never a cache (a cache would carry one
 * identity's answer across a support-session switch to the next).
 */
let inFlight: Promise<unknown> | null = null;

function readPreferences(): Promise<unknown> {
  if (inFlight !== null) return inFlight;
  // No signal on the shared read: aborting it would cancel it for every other
  // mount waiting on it. Each caller checks its own signal before using it.
  const request = api
    .getPreferences()
    .then((response) => response.data.preferences as unknown)
    .finally(() => {
      inFlight = null;
    });
  inFlight = request;
  return request;
}

function storedValue(preferences: unknown, pageKey: string): boolean {
  if (preferences === null || typeof preferences !== 'object') return false;
  const branch = (preferences as Record<string, unknown>)[SHOW_ALL_KEY];
  if (branch === null || typeof branch !== 'object' || Array.isArray(branch)) return false;
  return (branch as Record<string, unknown>)[pageKey] === true;
}

/**
 * Writes per page key run one after another, so two quick toggles cannot
 * arrive out of order and leave the stored value on the one pressed first.
 */
const writeChains = new Map<string, Promise<void>>();

function persist(pageKey: string, value: boolean): void {
  const previous = writeChains.get(pageKey) ?? Promise.resolve();
  const next = previous
    .then(() =>
      // `keepalive`, so a reload straight after the toggle does not cancel it.
      api.updatePreferences(
        { preferences: { [SHOW_ALL_KEY]: { [pageKey]: value ? true : null } } },
        undefined,
        true,
      ),
    )
    .then(
      () => undefined,
      () => {
        // A failed write leaves the choice applied on screen and unsaved; it is
        // not rolled back under the reader's hands.
      },
    );
  writeChains.set(pageKey, next);
  void next.finally(() => {
    if (writeChains.get(pageKey) === next) writeChains.delete(pageKey);
  });
}

/**
 * @returns `[showAll, setShowAll, loaded]`. `loaded` is `false` until the
 *   stored value has been read (or the read failed) — a page that wants to
 *   avoid fetching page 1 and then everything can wait on it. Destructuring
 *   the first two alone is the ordinary use.
 */
export function useShowAll(
  pageKey: string,
): [showAll: boolean, setShowAll: (value: boolean) => void, loaded: boolean] {
  const [showAll, setShowAllState] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Set once the reader has chosen; a read that lands later must not overrule
  // them.
  const touched = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    touched.current = false;
    readPreferences()
      .then((preferences) => {
        if (signal.aborted || touched.current) return;
        setShowAllState(storedValue(preferences, pageKey));
      })
      .catch(() => {
        if (signal.aborted || touched.current) return;
        setShowAllState(false);
      })
      .finally(() => {
        if (signal.aborted) return;
        setLoaded(true);
      });
    return () => controller.abort();
  }, [pageKey]);

  const setShowAll = useCallback(
    (value: boolean) => {
      touched.current = true;
      setLoaded(true);
      setShowAllState(value);
      persist(pageKey, value);
    },
    [pageKey],
  );

  return [showAll, setShowAll, loaded];
}

/** One page of a list endpoint, as `fetchAllPages` needs it. */
export interface ListPageResult<T> {
  data: T[];
  meta: { total: number; totalPages: number; page: number };
}

/** The server's ceiling for `perPage` on every list endpoint here. */
const PAGE_SIZE = 100;

/**
 * Every row matching a list query, fetched 100 at a time.
 *
 * Stops at a short page, at `meta.totalPages`, or at `cap` rows — whichever
 * comes first — and says which by `truncated`.
 *
 * ⛔ **THROWS ON ANY FAILED PAGE AND NEVER RETURNS A PARTIAL LIST.** A list
 * that stops early LOOKS complete, which is a false statement about the data
 * in the reassuring direction. The caller renders its error state instead.
 *
 * ⚠️ **A server that ignores `page` is caught on the second request** — it
 * would answer page 1 again, and the loop would otherwise collect the same
 * rows until the cap, presenting duplicates as a full list.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, perPage: 100, signal: AbortSignal) => Promise<ListPageResult<T>>,
  signal: AbortSignal,
  cap = 2000,
): Promise<{ rows: T[]; total: number; truncated: boolean }> {
  const rows: T[] = [];
  let total = 0;
  const maxRequests = Math.ceil(cap / PAGE_SIZE) + 1;

  for (let page = 1; page <= maxRequests; page += 1) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const result = await fetchPage(page, PAGE_SIZE, signal);
    if (result.meta.page !== page) {
      throw new Error(`The list answered page ${result.meta.page} when page ${page} was asked for.`);
    }
    if (page === 1) total = result.meta.total;
    rows.push(...result.data);

    if (rows.length >= cap) {
      const capped = rows.slice(0, cap);
      return { rows: capped, total, truncated: capped.length < total };
    }
    if (result.data.length < PAGE_SIZE || page >= result.meta.totalPages) break;
  }

  // Reached only by a short page or the last page — the cap returns above.
  return { rows, total, truncated: false };
}
